import { z } from "zod";

export const projectStatusSchema = z.enum(["ACTIVE", "ENDED", "ARCHIVED"]);
export const customerRelationSchema = z.enum(["CLIENT", "PARTNER", "OTHER"]);
export const participantSourceSchema = z.enum(["MANUAL", "BACKLOG", "SHEET"]);

export const projectCustomerViewSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  relation: customerRelationSchema,
  isPrimary: z.boolean(),
  note: z.string(),
});

export const projectParticipantViewSchema = z.object({
  /** 人を指すキー(SPEC §2.8)。外部の検出結果でメールが分からない場合は null */
  email: z.string().nullable(),
  /** 登録時点の表示名のコピー */
  displayName: z.string(),
  isAdmin: z.boolean(),
  source: participantSourceSchema,
  externalId: z.string().nullable(),
  active: z.boolean(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
});

/** プロジェクトの API 表現。revision は更新時にそのまま返す(楽観ロック)。 */
export const projectViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  description: z.string(),
  status: projectStatusSchema,
  customers: z.array(projectCustomerViewSchema),
  participants: z.array(projectParticipantViewSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  revision: z.number(),
  /** ログイン中のユーザーがこのプロジェクトを編集できるか。 */
  canEdit: z.boolean(),
});
export type ProjectView = z.infer<typeof projectViewSchema>;

export const projectListResponseSchema = z.object({ projects: z.array(projectViewSchema) });
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;
export const projectResponseSchema = z.object({ project: projectViewSchema });
export type ProjectResponse = z.infer<typeof projectResponseSchema>;

export const projectListQuerySchema = z.object({
  status: projectStatusSchema.optional(),
  /** true なら自分が参加しているプロジェクトだけ。 */
  mine: z.enum(["true", "false"]).optional(),
});

export const createProjectRequestSchema = z.object({
  name: z.string().trim().min(1, "プロジェクト名は必須です").max(200),
  code: z.string().trim().max(50).optional(),
  description: z.string().max(5000).optional(),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

export const updateProjectRequestSchema = z.object({
  revision: z.number().int().positive(),
  name: z.string().trim().min(1).max(200).optional(),
  code: z.string().trim().max(50).nullable().optional(),
  description: z.string().max(5000).optional(),
});
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

export const changeProjectStatusRequestSchema = z.object({
  revision: z.number().int().positive(),
  status: projectStatusSchema,
});
export type ChangeProjectStatusRequest = z.infer<typeof changeProjectStatusRequestSchema>;

export const setProjectCustomersRequestSchema = z.object({
  revision: z.number().int().positive(),
  customers: z.array(
    z.object({
      customerId: z.string(),
      relation: customerRelationSchema,
      isPrimary: z.boolean(),
      note: z.string().max(500).default(""),
    }),
  ),
});
export type SetProjectCustomersRequest = z.infer<typeof setProjectCustomersRequestSchema>;

/** 手動(MANUAL)の参加者だけを置き換える。Backlog 等から検出した行は維持される。 */
export const setProjectParticipantsRequestSchema = z.object({
  revision: z.number().int().positive(),
  participants: z.array(
    z
      .object({
        /** 分かっていれば入れる。メール不明の人は表示名だけで登録できる */
        email: z.email().optional(),
        displayName: z.string().trim().max(200).default(""),
        /** Backlog のユーザー ID など。メールが分からない人の識別に使う */
        externalId: z.string().trim().max(200).optional(),
        isAdmin: z.boolean(),
        active: z.boolean().default(true),
      })
      .refine((p) => p.email !== undefined || p.displayName !== "" || p.externalId !== undefined, {
        message: "メールアドレスか表示名が必要です",
      })
      .refine((p) => !p.isAdmin || p.email !== undefined, {
        message: "メールアドレスのない参加者は管理者にできません",
      }),
  ),
});
export type SetProjectParticipantsRequest = z.infer<typeof setProjectParticipantsRequestSchema>;
